import type { RefObject } from 'react';

import type {
	SelectionExtensionPlugin,
	SelectionExtensionPluginOptions,
	ExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { useEditorToolbarExtensions } from '@confluence/highlight-actions/entry-points/useEditorToolbarExtensions.ts';
import { useJiraWorkCreateSelectionExtension } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtension.ts';
import { useJiraWorkCreateSelectionExtensionV2 } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtensionV2.ts';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useTrelloInboxSelectionExtension } from './useTrelloInboxSelectionExtension.tsx';

export const useSelectionExtensionPluginOptions = ({
	isSidePanelOpenRef,
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	contentId,
	editorAPIRef,
	flags,
	snippetExtensionList = [],
}: {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	contentId: string | undefined;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
	flags?: FlagsStateContainer;
	snippetExtensionList?: ExtensionConfiguration[];
}): SelectionExtensionPluginOptions => {
	const jiraWorkCreateSelectionExtension = useJiraWorkCreateSelectionExtension({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
	});

	const jiraWorkCreateExtensionV2 = useJiraWorkCreateSelectionExtensionV2({
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		editorAPIRef,
	});

	// Connect and Forge 2P extensions
	const useExternalExtensionsWithContentId = () => useEditorToolbarExtensions(contentId);
	const useConditionalExternalExtensions = conditionalHooksFactory(
		() => expVal('confluence_enable_2p_toolbar_apps', 'isEnabled', false),
		useExternalExtensionsWithContentId,
		() => [],
	);

	const externalExtensions = useConditionalExternalExtensions();

	const useTrelloExtensionWithArgs = () => [
		useTrelloInboxSelectionExtension(contentId, editorAPIRef, flags),
	];
	const useConditionalTrelloExtension = conditionalHooksFactory(
		() => fg('quick_capture_trello_inbox'),
		useTrelloExtensionWithArgs,
		() => [] as ExtensionConfiguration[],
	);
	const trelloExtensionList = useConditionalTrelloExtension();
	const jiraWorkCreateExtensionList = expVal(
		'confluence-bulk-work-create-editor',
		'isEnabled',
		false,
	)
		? jiraWorkCreateExtensionV2
		: [];

	const extensionList = [
		...snippetExtensionList,
		...externalExtensions,
		...jiraWorkCreateExtensionList,
		...trelloExtensionList,
	];

	return {
		extensions: expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
			? {}
			: { firstParty: [jiraWorkCreateSelectionExtension] },
		extensionList,
	};
};
